<template>
  <div class="mainfont mainbg2 dashboard">
    <v-snackbar v-model="showSnackBar" color="black">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#283e51"
      spinner="spinner"
    />

    <!-- Hero header -->
    <section class="hero">
      <div class="hero-decor hero-decor--one"></div>
      <div class="hero-decor hero-decor--two"></div>
      <div class="hero-content">
        <div class="hero-text">
          <div class="hero-date">
            <v-icon small color="rgba(255,255,255,0.85)" class="mr-1">mdi-calendar-today</v-icon>
            {{ today }}
          </div>
          <h1 class="hero-title">{{ greeting }}, {{ displayName }}</h1>
          <p class="hero-subtitle">
            Here's what's happening across your nature camps today.
          </p>
        </div>
        <div class="hero-actions">
          <v-btn
            depressed
            rounded
            color="white"
            class="hero-btn hero-btn--primary"
            :to="config.pendingRoute"
          >
            <v-icon left small>mdi-clipboard-check-outline</v-icon>
            Review pending
            <span v-if="pendingCount > 0" class="hero-badge">{{ pendingCount }}</span>
          </v-btn>
          <v-btn
            v-if="config.secondaryAction"
            depressed
            rounded
            outlined
            color="white"
            class="hero-btn"
            :to="config.secondaryAction.to"
          >
            <v-icon left small>{{ config.secondaryAction.icon }}</v-icon>
            {{ config.secondaryAction.label }}
          </v-btn>
        </div>
      </div>
    </section>

    <!-- Nature Camps Showcase (Section Clerk only) -->
    <section v-if="isClerk" class="camp-showcase-section">
      <v-card flat class="camp-showcase-card" :to="campsRoute">
        <div class="camp-showcase-overlay"></div>
        <div class="camp-showcase-content">
          <div class="camp-showcase-left">
            <div class="camp-showcase-icon-wrap">
              <v-icon color="#ffffff" size="30">mdi-pine-tree</v-icon>
            </div>
            <div class="camp-showcase-info">
              <div class="camp-showcase-tag">
                <v-icon x-small color="#059669" class="mr-1">mdi-sprout</v-icon>
                Division Nature Camps
              </div>
              <h2 class="camp-showcase-title">Active Nature Camps</h2>
            </div>
          </div>
          <div class="camp-showcase-right">
            <div class="camp-showcase-metric-box">
              <div class="camp-showcase-count-wrap">
                <span class="camp-showcase-count"><count-up :value="Number(natureCampsCount)" /></span>
              </div>
              <div class="camp-showcase-metric-label">Total Nature Camps</div>
              <div class="camp-showcase-status-badge">
                <span class="status-pulse-dot"></span>
                Active Sites
              </div>
            </div>
            <div class="camp-showcase-footer">
              <span>View nature camps</span>
              <v-icon small class="camp-showcase-arrow">mdi-arrow-right</v-icon>
            </div>
          </div>
        </div>
      </v-card>
    </section>

    <!-- Primary KPIs -->
    <section class="kpi-grid" :class="{ 'kpi-grid--three': isClerk }">
      <v-card
        v-for="kpi in displayedKpis"
        :key="kpi.label"
        :to="kpi.to"
        flat
        class="kpi-card"
        :class="{ 'kpi-card--static': !kpi.to }"
        :style="{ '--accent': kpi.color, '--accent-soft': kpi.soft }"
      >
        <div class="kpi-top">
          <div class="kpi-icon">
            <v-icon color="white">{{ kpi.icon }}</v-icon>
          </div>
          <span v-if="kpi.alert" class="kpi-alert">
            <span class="kpi-alert-dot"></span>
            Needs attention
          </span>
        </div>
        <div class="kpi-value"><count-up :value="toNum(kpi.value)" /></div>
        <div class="kpi-label">{{ kpi.label }}</div>
        <div v-if="kpi.to" class="kpi-footer">
          <span>{{ kpi.action }}</span>
          <v-icon small class="kpi-arrow">mdi-arrow-right</v-icon>
        </div>
      </v-card>
    </section>

    <!-- Secondary stats -->
    <section v-if="miniStats.length" class="mini-grid">
      <component
        :is="stat.to ? 'router-link' : 'div'"
        v-for="stat in miniStats"
        :key="stat.label"
        :to="stat.to"
        class="mini-card"
        :class="{ 'mini-card--link': stat.to }"
      >
        <div class="mini-icon" :style="{ color: stat.color, background: stat.soft }">
          <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
        </div>
        <div class="mini-body">
          <div class="mini-value"><count-up :value="toNum(stat.value)" /></div>
          <div class="mini-label">{{ stat.label }}</div>
        </div>
      </component>
    </section>

    <!-- Booking Calendar -->
    <v-card flat class="chart-card booking-cal">
      <!-- Header row -->
      <div class="cal-header">
        <div class="cal-title-block">
          <div class="chart-title">
            Booking Calendar
            <span class="chart-year">{{ calYear }}</span>
          </div>
          <div class="chart-subtitle">View camp bookings by date</div>
        </div>

        <!-- Camp filter + nav on the right -->
        <div class="cal-controls">
          <!-- Camp filter dropdown -->
          <div class="cal-camp-select-wrap">
            <v-select
              v-model="calSelectedCampId"
              :items="calCamps"
              item-text="campName"
              item-value="_id"
              dense
              outlined
              hide-details
              class="cal-camp-select"
              :loading="calLoading"
              :disabled="calLoading || calCamps.length === 0"
              placeholder="Select nature camp"
              @change="onCalCampChange"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="#13736f" class="mt-1">mdi-pine-tree</v-icon>
              </template>
            </v-select>
          </div>

          <!-- Month & Year navigation with dropdowns -->
          <div class="cal-nav">
            <v-btn
              icon small
              class="cal-nav-btn"
              :disabled="calYear <= calMinYear && calMonth === 0"
              @click="calPrevMonth"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div class="cal-nav-label-wrap">
              <!-- Month Dropdown -->
              <v-menu
                v-model="calMonthMenu"
                offset-y
                transition="slide-y-transition"
                min-width="140"
                max-height="300"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="cal-picker-btn"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span>{{ monthNames[calMonth] }}</span>
                    <v-icon x-small class="cal-picker-icon">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="cal-picker-list">
                  <v-list-item
                    v-for="(name, idx) in monthNames"
                    :key="name"
                    class="cal-picker-item"
                    :class="{ 'cal-picker-item--active': calMonth === idx }"
                    @click="onSelectMonth(idx)"
                  >
                    <v-list-item-title>{{ name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <!-- Year Dropdown -->
              <v-menu
                v-model="calYearMenu"
                offset-y
                transition="slide-y-transition"
                min-width="110"
                max-height="300"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="cal-picker-btn"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span>{{ calYear }}</span>
                    <v-icon x-small class="cal-picker-icon">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="cal-picker-list">
                  <v-list-item
                    v-for="yr in yearOptions"
                    :key="yr"
                    class="cal-picker-item"
                    :class="{ 'cal-picker-item--active': calYear === yr }"
                    @click="onSelectYear(yr)"
                  >
                    <v-list-item-title>{{ yr }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-btn
              icon small
              class="cal-nav-btn"
              :disabled="calYear >= calMaxYear && calMonth === 11"
              @click="calNextMonth"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              small depressed rounded
              color="#13736f"
              dark
              class="ml-2 cal-today-btn"
              @click="calGoToday"
            >Today</v-btn>
          </div>
        </div>
      </div>

      <!-- Loading / empty states -->
      <div v-if="calLoading" class="chart-placeholder">
        <v-progress-circular indeterminate color="#13736f" />
      </div>
      <div v-else-if="calError" class="chart-placeholder">
        <v-icon large color="#cbd5e1">mdi-calendar-remove</v-icon>
        <p>Couldn't load calendar data.</p>
        <v-btn small text color="#13736f" @click="getCalendar">Try again</v-btn>
      </div>
      <div v-else-if="calCamps.length === 0" class="chart-placeholder">
        <v-icon large color="#cbd5e1">mdi-pine-tree-box</v-icon>
        <p>No nature camps available.</p>
      </div>

      <!-- Calendar grid -->
      <div v-else class="cal-grid-wrap">
        <!-- Day-of-week headers -->
        <div class="cal-dow-row">
          <div v-for="d in ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']" :key="d" class="cal-dow">{{ d }}</div>
        </div>

        <!-- Weeks -->
        <div class="cal-weeks">
          <div v-for="(week, wi) in calWeeks" :key="wi" class="cal-week">
            <div
              v-for="(day, di) in week"
              :key="di"
              class="cal-cell"
              :class="{
                'cal-cell--faded': !day.inMonth,
                'cal-cell--today': day.isToday,
              }"
            >
              <div class="cal-day-num" :class="{ 'cal-day-num--today': day.isToday }">
                {{ day.num }}
              </div>

              <!-- Booking span bars -->
              <template v-if="day.inMonth">
                <div
                  v-for="span in day.spans"
                  :key="span.spanId"
                  class="cal-span-bar"
                  :class="{
                    'cal-span-bar--start':  span.isStart,
                    'cal-span-bar--end':    span.isEnd,
                    'cal-span-bar--middle': !span.isStart && !span.isEnd,
                    'cal-span-bar--single': span.isStart && span.isEnd,
                    'cal-span-bar--held':   span.held,
                  }"
                  :style="{ '--span-color': span.color, '--span-color-light': span.colorLight }"
                  :title="spanTitle(span)"
                >
                  <span v-if="span.isStart" class="cal-span-name">
                    <v-icon v-if="span.held" x-small class="cal-held-icon">mdi-timer-sand</v-icon>{{ span.name }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="calHasHeld" class="cal-held-note">
          <span class="cal-held-swatch"></span>
          Held for a few minutes while the applicant completes the form — not confirmed yet
        </div>

        <!-- Legend -->
        <!-- <div v-if="calLegend.length" class="cal-legend">
          <div
            v-for="entry in calLegend"
            :key="entry.name"
            class="cal-legend-item"
          >
            <span class="cal-legend-dot" :style="{ background: entry.color }"></span>
            <span class="cal-legend-name">{{ entry.name }}</span>
          </div>
        </div> -->
      </div>
    </v-card>

    <!-- Payments (head office only) -->
    <template v-if="config.paymentsUrl">
      <section class="payment-stats">
        <div v-for="stat in paymentStats" :key="stat.label" class="payment-stat">
          <div class="payment-stat-icon" :style="{ color: stat.color, background: stat.soft }">
            <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
          </div>
          <div class="payment-stat-body">
            <div class="payment-stat-value">
              <template v-if="paymentsLoading">…</template>
              <count-up
                v-else
                :value="stat.value"
                :formatter="stat.currency ? formatCurrency : undefined"
              />
            </div>
            <div class="payment-stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </section>

      <section class="charts-grid">
        <v-card flat class="chart-card">
          <div class="chart-header">
            <div>
              <div class="chart-title">
                Payment Collections
                <span class="chart-year">{{ currentYear }}</span>
              </div>
              <div class="chart-subtitle">Amount collected and successful payments per month</div>
            </div>
            <v-btn small text color="#13736f" class="text-none font-weight-bold" to="/completed-payments">
              View payments
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
          <div v-if="paymentsLoading" class="chart-placeholder">
            <v-progress-circular indeterminate color="#13736f" />
          </div>
          <div v-else-if="paymentsError" class="chart-placeholder">
            <v-icon large color="#cbd5e1">mdi-cash-multiple</v-icon>
            <p>Couldn't load payment data.</p>
            <v-btn small text color="#13736f" @click="getPayments">Try again</v-btn>
          </div>
          <div v-else-if="yearPayments.length > 0">
            <apexchart
              type="line"
              :height="chartHeight"
              :options="paymentChartOptions"
              :series="paymentSeries"
            />
          </div>
          <div v-else class="chart-placeholder">
            <v-icon large color="#cbd5e1">mdi-cash-multiple</v-icon>
            <p>No payments received yet this year.</p>
          </div>
        </v-card>

        <v-card flat class="chart-card">
          <div class="chart-header">
            <div>
              <div class="chart-title">Collection by Division</div>
              <div class="chart-subtitle">Share of {{ currentYear }} collections</div>
            </div>
          </div>
          <div v-if="paymentsLoading" class="chart-placeholder">
            <v-progress-circular indeterminate color="#13736f" />
          </div>
          <div v-else-if="divisionBreakdown.length > 0">
            <apexchart
              type="donut"
              :height="chartHeight"
              :options="divisionChartOptions"
              :series="divisionSeries"
            />
          </div>
          <div v-else class="chart-placeholder">
            <v-icon large color="#cbd5e1">mdi-chart-donut</v-icon>
            <p>{{ paymentsError ? "Couldn't load payment data." : "No collections to show yet." }}</p>
          </div>
        </v-card>
      </section>
    </template>
  </div>
</template>
<script>
import axios from "axios";
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
import { getDashboardConfig } from "@/components/Common/dashboardConfig";
import { getStoredUsername, USERNAME_EVENT } from "@/utils/userName";

// Animates from the previous value to the new one (ease-out).
const CountUp = {
  props: {
    value: { type: Number, default: null },
    duration: { type: Number, default: 1200 },
    formatter: { type: Function, default: (v) => Number(v).toLocaleString("en-IN") },
  },
  data() {
    return { display: 0, raf: null };
  },
  watch: {
    value: {
      immediate: true,
      handler(to) {
        if (typeof to !== "number" || isNaN(to)) return;
        this.animate(this.display, to);
      },
    },
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
  },
  methods: {
    animate(from, to) {
      cancelAnimationFrame(this.raf);
      const start = performance.now();
      const step = (now) => {
        const t = Math.min((now - start) / this.duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        this.display = from + (to - from) * eased;
        if (t < 1) this.raf = requestAnimationFrame(step);
        else this.display = to;
      };
      this.raf = requestAnimationFrame(step);
    },
  },
  render(h) {
    if (typeof this.value !== "number" || isNaN(this.value)) return h("span", "—");
    return h("span", this.formatter(Math.round(this.display)));
  },
};

export default {
  components: {
    apexchart: VueApexCharts,
    CountUp,
  },
  props: {
    role: { type: String, default: "admin" },
  },
  data() {
    return {
      storedName: getStoredUsername(),
      appLoading: false,
      graphLoading: true,
      graphError: false,
      barlist: [],
      payments: [],
      paymentsLoading: false,
      paymentsError: false,
      list: {},
      msg: null,
      showSnackBar: false,
      // --- Booking Calendar ---
      calLoading: false,
      calError: false,
      calCamps: [],          // [{_id, campName, district}, ...] sorted A-Z
      calSelectedCampId: null,
      calYear: new Date().getFullYear(),
      calMonth: new Date().getMonth(), // 0-based
      calDays: {},           // { "YYYY-MM-DD": [{name, memberCount}] }
      calBookingSpans: [],   // [{id, name, startDate, endDate, memberCount}]
      calMonthMenu: false,
      calYearMenu: false,
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
  },
  computed: {
    today() {
      return moment().format("dddd, D MMMM YYYY");
    },
    greeting() {
      const hour = moment().hour();
      if (hour < 12) return "Good morning";
      if (hour < 17) return "Good afternoon";
      return "Good evening";
    },
    config() {
      return getDashboardConfig(this.role);
    },
    // Full name as saved on the profile page (not just the first word)
    displayName() {
      return this.storedName || "there";
    },
    pendingCount() {
      return this.list[this.config.pendingField] || 0;
    },
    isClerk() {
      return this.role === "clerk" || localStorage.getItem("uType") === "7741";
    },
    natureCampsCount() {
      const camp = this.kpis.find((kpi) => kpi.label === "Nature Camps");
      if (camp && camp.value !== undefined && camp.value !== null) return camp.value;
      return this.list.totalNatureCamps || 0;
    },
    campsRoute() {
      const camp = this.kpis.find((kpi) => kpi.label === "Nature Camps");
      return (camp && camp.to) || "/divisionNatureCamp";
    },
    natureCampKpi() {
      return this.kpis.find((kpi) => kpi.label === "Nature Camps") || {
        label: "Nature Camps",
        value: this.natureCampsCount,
        icon: "mdi-pine-tree",
        color: "#059669",
        soft: "rgba(16, 185, 129, 0.12)",
      };
    },
    displayedKpis() {
      if (this.isClerk) {
        return this.kpis.filter((kpi) => kpi.label !== "Nature Camps");
      }
      return this.kpis;
    },
    kpis() {
      return this.config.kpis.map((kpi) => ({
        ...kpi,
        value: kpi.field === "approvedCount" ? this.list[kpi.field] || 0 : this.list[kpi.field],
        alert: kpi.alert && this.pendingCount > 0,
      }));
    },
    miniStats() {
      return this.config.miniStats.map((stat) => ({
        ...stat,
        value: this.list[stat.field],
      }));
    },
    graphYear() {
      return this.barlist.length > 0 ? this.barlist[0].year : null;
    },
    totalBookings() {
      return this.barlist.reduce((sum, item) => sum + item.bookings, 0);
    },
    peakMonth() {
      if (this.totalBookings === 0) return "—";
      const peak = this.barlist.reduce((a, b) => (b.bookings > a.bookings ? b : a));
      return peak.month.slice(0, 3);
    },
    avgPerMonth() {
      const monthsElapsed = moment().month() + 1;
      const avg = this.totalBookings / monthsElapsed;
      return Number.isInteger(avg) ? avg : avg.toFixed(1);
    },
    // --- Calendar computed ---
    calMonthLabel() {
      return moment({ year: this.calYear, month: this.calMonth }).format("MMMM YYYY");
    },
    calMinYear() {
      return 2020;
    },
    calMaxYear() {
      // Always allow a few years ahead so future bookings stay reachable
      return new Date().getFullYear() + 5;
    },
    yearOptions() {
      const list = [];
      for (let y = this.calMinYear; y <= this.calMaxYear; y++) {
        list.push(y);
      }
      return list;
    },
    // Color palette for booking spans
    calColorPalette() {
      return [
        { color: '#7c1fd1', light: 'rgba(168,85,247,0.18)' },  // violet
        { color: '#e11d48', light: 'rgba(244,63,94,0.16)'  },  // red-pink
        { color: '#d97706', light: 'rgba(217,119,6,0.14)'  },  // amber
        { color: '#0891b2', light: 'rgba(8,145,178,0.14)'  },  // cyan
        { color: '#ea580c', light: 'rgba(234,88,12,0.14)'  },  // orange
        { color: '#16a34a', light: 'rgba(22,163,74,0.14)'  },  // green
        { color: '#9333ea', light: 'rgba(147,51,234,0.14)' },  // purple
        { color: '#0284c7', light: 'rgba(2,132,199,0.14)'  },  // blue
        { color: '#be123c', light: 'rgba(190,18,60,0.14)'  },  // rose
        { color: '#13736f', light: 'rgba(19,115,111,0.14)' },  // brand teal
      ];
    },
    // Map unique applicant name -> { color, light }
    calNameColorMap() {
      const map = {};
      const palette = this.calColorPalette;
      let idx = 0;
      this.calBookingSpans.forEach((span) => {
        if (span.held) return;
        if (!map[span.name]) {
          map[span.name] = palette[idx % palette.length];
          idx++;
        }
      });
      return map;
    },
    // Legend entries (one per unique name)
    calLegend() {
      return Object.entries(this.calNameColorMap).map(([name, c]) => ({
        name,
        color: c.color,
      }));
    },
    calHasHeld() {
      return this.calBookingSpans.some((s) => s.held);
    },
    calWeeks() {
      const year = this.calYear;
      const month = this.calMonth;
      const today = moment().startOf("day");

      const firstDay = moment({ year, month, day: 1 });
      let startOffset = firstDay.isoWeekday() - 1;

      const daysInMonth = firstDay.daysInMonth();
      const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

      // Build spansByDate lookup
      const spansByDate = {};
      const nameColorMap = this.calNameColorMap;

      this.calBookingSpans.forEach((span) => {
        const colorInfo = span.held
          ? { color: '#b45309', light: 'rgba(245,158,11,0.18)' }
          : nameColorMap[span.name] || { color: '#13736f', light: 'rgba(19,115,111,0.14)' };
        let cur = moment(span.startDate, 'YYYY-MM-DD');
        const endM = moment(span.endDate, 'YYYY-MM-DD');

        while (cur.isSameOrBefore(endM, 'day')) {
          const key = cur.format('YYYY-MM-DD');
          if (!spansByDate[key]) spansByDate[key] = [];
          spansByDate[key].push({
            spanId:      span.id,
            name:        span.name,
            memberCount: span.memberCount,
            held:        !!span.held,
            holdUntil:   span.holdUntil,
            color:       colorInfo.color,
            colorLight:  colorInfo.light,
            isStart: cur.isSame(moment(span.startDate, 'YYYY-MM-DD'), 'day'),
            isEnd:   cur.isSame(endM, 'day'),
          });
          cur.add(1, 'day');
        }
      });

      const weeks = [];
      let current = firstDay.clone().subtract(startOffset, "days");

      for (let i = 0; i < totalCells; i++) {
        const weekIdx = Math.floor(i / 7);
        if (!weeks[weekIdx]) weeks[weekIdx] = [];
        const inMonth = current.month() === month && current.year() === year;
        const dateKey = current.format("YYYY-MM-DD");
        weeks[weekIdx].push({
          num: current.date(),
          inMonth,
          isToday: current.isSame(today, "day"),
          bookings: inMonth ? (this.calDays[dateKey] || []) : [],
          spans:    inMonth ? (spansByDate[dateKey] || []) : [],
        });
        current.add(1, "day");
      }
      return weeks;
    },
    chartHeight() {
      if (this.$vuetify.breakpoint.xsOnly) return 260;
      if (this.$vuetify.breakpoint.smAndDown) return 300;
      return 360;
    },
    series() {
      return [
        {
          name: "Bookings",
          data: this.barlist.map((item) => item.bookings),
        },
      ];
    },
    chartOptions() {
      const currentMonth = moment().month();
      const maxValue = Math.max(...this.barlist.map((item) => item.bookings), 0);
      return {
        chart: {
          id: "barmonth",
          type: "line",
          background: "transparent",
          fontFamily: "inherit",
          toolbar: { show: false },
          zoom: { enabled: false },
          dropShadow: {
            enabled: true,
            top: 6,
            left: 0,
            blur: 8,
            color: "#13736f",
            opacity: 0.18,
          },
        },
        colors: ["#13736f"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
          colors: ["#ffffff"],
          strokeColors: "#13736f",
          strokeWidth: 2,
          hover: { size: 7 },
          discrete: [
            {
              seriesIndex: 0,
              dataPointIndex: currentMonth,
              fillColor: "#f59e0b",
              strokeColor: "#ffffff",
              size: 8,
            },
          ],
        },
        legend: { show: false },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#eef2f6",
          strokeDashArray: 4,
          padding: { left: 8, right: 16 },
        },
        xaxis: {
          categories: this.barlist.map((item) => item.month.slice(0, 3)),
          axisBorder: { show: false },
          axisTicks: { show: false },
          crosshairs: {
            stroke: { color: "#13736f", width: 1, dashArray: 4 },
          },
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
            style: { colors: "#64748b", fontSize: "12px", fontWeight: 600 },
          },
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          decimalsInFloat: 0,
          tickAmount: Math.max(Math.min(maxValue, 5), 1),
          labels: {
            style: { colors: "#94a3b8", fontSize: "12px" },
          },
        },
        tooltip: {
          theme: "light",
          x: {
            formatter: (val, opts) =>
              `${this.barlist[opts.dataPointIndex].month} ${this.graphYear}`,
          },
          y: {
            formatter: (val) => `${val} booking${val === 1 ? "" : "s"}`,
          },
        },
      };
    },
    currentYear() {
      return moment().year();
    },
    yearPayments() {
      return this.payments
        .filter((p) => p.PAYMENT_DATE && moment(p.PAYMENT_DATE).year() === this.currentYear)
        .map((p) => ({
          amount: Number(p.CHALLAN_AMOUNT) || 0,
          month: moment(p.PAYMENT_DATE).month(),
          division: p.DIVISION_NAME || "Unassigned",
        }));
    },
    totalCollected() {
      return this.yearPayments.reduce((sum, p) => sum + p.amount, 0);
    },
    paymentStats() {
      const count = this.yearPayments.length;
      const thisMonth = moment().month();
      const monthTotal = this.yearPayments
        .filter((p) => p.month === thisMonth)
        .reduce((sum, p) => sum + p.amount, 0);
      return [
        {
          label: `Collected in ${this.currentYear}`,
          value: this.totalCollected,
          currency: true,
          icon: "mdi-cash-multiple",
          color: "#13736f",
          soft: "rgba(19, 115, 111, 0.1)",
        },
        {
          label: `Collected in ${moment().format("MMMM")}`,
          value: monthTotal,
          currency: true,
          icon: "mdi-calendar-month-outline",
          color: "#d97706",
          soft: "rgba(245, 158, 11, 0.12)",
        },
        {
          label: "Successful payments",
          value: count,
          icon: "mdi-check-circle-outline",
          color: "#059669",
          soft: "rgba(16, 185, 129, 0.12)",
        },
        {
          label: "Average payment",
          value: count ? this.totalCollected / count : null,
          currency: true,
          icon: "mdi-scale-balance",
          color: "#2563eb",
          soft: "rgba(59, 130, 246, 0.12)",
        },
      ];
    },
    paymentSeries() {
      const amounts = new Array(12).fill(0);
      const counts = new Array(12).fill(0);
      this.yearPayments.forEach((p) => {
        amounts[p.month] += p.amount;
        counts[p.month] += 1;
      });
      return [
        { name: "Amount collected", type: "column", data: amounts },
        { name: "Payments", type: "line", data: counts },
      ];
    },
    paymentChartOptions() {
      return {
        chart: {
          id: "payments",
          background: "transparent",
          fontFamily: "inherit",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#13736f", "#f59e0b"],
        stroke: { width: [0, 3], curve: "smooth" },
        plotOptions: {
          bar: {
            borderRadius: 5,
            columnWidth: this.$vuetify.breakpoint.xsOnly ? "65%" : "45%",
          },
        },
        fill: {
          type: ["gradient", "solid"],
          gradient: {
            type: "vertical",
            shadeIntensity: 0.2,
            opacityFrom: 1,
            opacityTo: 0.7,
            stops: [0, 100],
          },
        },
        markers: {
          size: [0, 4],
          colors: ["#ffffff"],
          strokeColors: "#f59e0b",
          strokeWidth: 2,
        },
        dataLabels: { enabled: false },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "12px",
          fontWeight: 600,
          labels: { colors: "#475569" },
          markers: { radius: 4 },
        },
        grid: {
          borderColor: "#eef2f6",
          strokeDashArray: 4,
          padding: { left: 8, right: 8 },
        },
        xaxis: {
          categories: moment.monthsShort(),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
            style: { colors: "#64748b", fontSize: "12px", fontWeight: 600 },
          },
        },
        yaxis: [
          {
            min: 0,
            forceNiceScale: true,
            labels: {
              formatter: (val) => this.formatCompactCurrency(val),
              style: { colors: "#94a3b8", fontSize: "12px" },
            },
          },
          {
            opposite: true,
            min: 0,
            forceNiceScale: true,
            decimalsInFloat: 0,
            show: !this.$vuetify.breakpoint.xsOnly,
            labels: {
              formatter: (val) => Math.round(val),
              style: { colors: "#d97706", fontSize: "12px" },
            },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          theme: "light",
          x: {
            formatter: (val, opts) =>
              `${moment.months()[opts.dataPointIndex]} ${this.currentYear}`,
          },
          y: {
            formatter: (val, opts) =>
              opts.seriesIndex === 0
                ? this.formatCurrency(val)
                : `${val} payment${val === 1 ? "" : "s"}`,
          },
        },
      };
    },
    divisionBreakdown() {
      const totals = {};
      this.yearPayments.forEach((p) => {
        totals[p.division] = (totals[p.division] || 0) + p.amount;
      });
      const sorted = Object.keys(totals)
        .map((name) => ({ name, amount: totals[name] }))
        .filter((d) => d.amount > 0)
        .sort((a, b) => b.amount - a.amount);
      if (sorted.length <= 6) return sorted;
      const others = sorted.slice(5).reduce((sum, d) => sum + d.amount, 0);
      return sorted.slice(0, 5).concat([{ name: "Others", amount: others }]);
    },
    divisionSeries() {
      return this.divisionBreakdown.map((d) => d.amount);
    },
    divisionChartOptions() {
      return {
        chart: {
          id: "division-collections",
          background: "transparent",
          fontFamily: "inherit",
        },
        labels: this.divisionBreakdown.map((d) => d.name),
        colors: ["#13736f", "#f59e0b", "#2563eb", "#7c3aed", "#0891b2", "#94a3b8"],
        stroke: { width: 3, colors: ["#ffffff"] },
        dataLabels: { enabled: false },
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontWeight: 600,
          labels: { colors: "#475569" },
          markers: { radius: 4 },
          itemMargin: { horizontal: 8, vertical: 4 },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { fontSize: "13px", color: "#64748b", offsetY: -4 },
                value: {
                  fontSize: "20px",
                  fontWeight: 800,
                  color: "#0f172a",
                  offsetY: 4,
                  formatter: (val) => this.formatCompactCurrency(Number(val)),
                },
                total: {
                  show: true,
                  label: "Total",
                  color: "#64748b",
                  formatter: () => this.formatCompactCurrency(this.totalCollected),
                },
              },
            },
          },
        },
        tooltip: {
          y: { formatter: (val) => this.formatCurrency(val) },
        },
      };
    },
  },
  mounted() {
    this.getList();
    this.getGraph();
    if (this.config.paymentsUrl) this.getPayments();
    this.getCalendar();
    window.addEventListener(USERNAME_EVENT, this.onUsernameChanged);
  },
  beforeDestroy() {
    window.removeEventListener(USERNAME_EVENT, this.onUsernameChanged);
  },
  methods: {
    onUsernameChanged(e) {
      this.storedName = (e && e.detail) || getStoredUsername();
    },
    formatCurrency(value) {
      return "₹" + Math.round(value || 0).toLocaleString("en-IN");
    },
    formatCompactCurrency(value) {
      const v = value || 0;
      if (v >= 10000000) return `₹${+(v / 10000000).toFixed(1)}Cr`;
      if (v >= 100000) return `₹${+(v / 100000).toFixed(1)}L`;
      if (v >= 1000) return `₹${+(v / 1000).toFixed(1)}K`;
      return `₹${Math.round(v)}`;
    },
    getPayments() {
      this.paymentsLoading = true;
      this.paymentsError = false;
      axios({
        method: "POST",
        url: this.config.paymentsUrl,
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {},
      })
        .then((response) => {
          if (response.data.status) {
            this.payments = response.data.data || [];
          } else {
            this.paymentsError = true;
          }
          this.paymentsLoading = false;
        })
        .catch((err) => {
          this.paymentsError = true;
          this.paymentsLoading = false;
          console.log(err);
        });
    },
    toNum(value) {
      return value === undefined || value === null || value === "" ? null : Number(value);
    },
    formatNumber(value) {
      if (value === undefined || value === null) return "—";
      return Number(value).toLocaleString("en-IN");
    },
    getGraph() {
      this.graphLoading = true;
      this.graphError = false;
      axios({
        method: "POST",
        url: this.config.graphUrl,
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.barlist = response.data.data || [];
          } else {
            this.graphError = true;
          }
          this.graphLoading = false;
        })
        .catch((err) => {
          this.graphError = true;
          this.graphLoading = false;
          console.log(err);
        });
    },
    getList() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: this.config.countsUrl,
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.list = response.data;
          this.appLoading = false;
          if (this.config.pendingCountUrl) this.getPendingCount();
        })
        .catch((err) => {
          this.appLoading = false;
          this.msg = "Couldn't load dashboard counts. Please try again.";
          this.showSnackBar = true;
          console.log(err);
        });
    },
    // Applications waiting at this user's own desk.
    getPendingCount() {
      axios({
        method: "GET",
        url: this.config.pendingCountUrl,
        headers: {
          token: localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.$set(this.list, this.config.pendingField, response.data.count || 0);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
  // ---------- Calendar methods ----------
    getCalendar(campId) {
      this.calLoading = true;
      this.calError = false;
      const payload = {
        month: this.calMonth,
        year: this.calYear,
      };
      if (campId || this.calSelectedCampId) {
        payload.campid = campId || this.calSelectedCampId;
      }
      axios({
        method: "POST",
        url: "/dashboard/booking/calendar",
        headers: { token: localStorage.getItem("token") },
        data: payload,
      })
        .then((response) => {
          if (response.data.status) {
            this.calCamps = response.data.camps || [];
            this.calDays = response.data.days || {};
            this.calBookingSpans = response.data.bookingSpans || [];
            // Sync the selected camp (backend resolves default = first alphabetically)
            if (response.data.selectedCampId) {
              this.calSelectedCampId = response.data.selectedCampId;
            } else if (this.calCamps.length > 0 && !this.calSelectedCampId) {
              this.calSelectedCampId = this.calCamps[0]._id;
            }
            this.calYear = response.data.year;
            this.calMonth = response.data.month;
          } else {
            this.calError = true;
          }
          this.calLoading = false;
        })
        .catch((err) => {
          this.calError = true;
          this.calLoading = false;
          console.error(err);
        });
    },
    spanTitle(span) {
      let t = span.name + (span.memberCount ? " (" + span.memberCount + " members)" : "");
      if (span.held) {
        t += " — on hold" + (span.holdUntil ? " until " + moment(span.holdUntil).format("hh:mm A") : "");
      }
      return t;
    },
    onCalCampChange(newId) {
      this.calSelectedCampId = newId;
      this.getCalendar(newId);
    },
    calPrevMonth() {
      if (this.calYear <= this.calMinYear && this.calMonth === 0) {
        return;
      }
      if (this.calMonth === 0) {
        this.calMonth = 11;
        this.calYear -= 1;
      } else {
        this.calMonth -= 1;
      }
      this.getCalendar();
    },
    calNextMonth() {
      if (this.calYear >= this.calMaxYear && this.calMonth === 11) {
        return;
      }
      if (this.calMonth === 11) {
        this.calMonth = 0;
        this.calYear += 1;
      } else {
        this.calMonth += 1;
      }
      this.getCalendar();
    },
    calGoToday() {
      this.calYear = new Date().getFullYear();
      this.calMonth = new Date().getMonth();
      this.getCalendar();
    },
    onSelectMonth(idx) {
      this.calMonth = idx;
      this.calMonthMenu = false;
      this.getCalendar();
    },
    onSelectYear(yr) {
      this.calYear = yr;
      this.calYearMenu = false;
      this.getCalendar();
    },
  },
};
</script>

<style scoped>
.dashboard {
  padding: 24px;
  min-height: 100%;
}

/* ---------- Hero ---------- */
.hero {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 28px 32px;
  margin-bottom: 24px;
  background: linear-gradient(120deg, #0f5f5c 0%, #13736f 45%, #2a9d8f 100%);
  box-shadow: 0 16px 40px rgba(19, 115, 111, 0.22);
  color: #ffffff;
}

.hero-decor {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}

.hero-decor--one {
  width: 260px;
  height: 260px;
  top: -120px;
  right: -60px;
}

.hero-decor--two {
  width: 160px;
  height: 160px;
  bottom: -90px;
  right: 220px;
}

.hero-content {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.hero-date {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.12);
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 12px;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin-bottom: 4px;
}

.hero-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 0;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  height: 42px !important;
  padding: 0 20px !important;
}

.hero-btn--primary {
  color: #13736f !important;
}

.hero-badge {
  margin-left: 8px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f59e0b;
  color: #ffffff;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
}

/* ---------- Nature Camps Showcase Card (Section Clerk) ---------- */
.camp-showcase-section {
  margin-bottom: 22px;
}

.camp-showcase-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px !important;
  background-color: #f0fbf7 !important;
  background-image: url('/bgsilhoute.png') !important;
  background-position: right bottom !important;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  border: 1px solid #cceee1 !important;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.08) !important;
  padding: 24px 34px !important;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease !important;
}

.camp-showcase-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, #059669 0%, #10b981 100%);
  border-radius: 4px 0 0 4px;
  z-index: 2;
}

.camp-showcase-card:hover {
  transform: translateY(-2px);
  border-color: #a7e3cb !important;
  box-shadow: 0 16px 38px rgba(16, 185, 129, 0.14) !important;
}

.camp-showcase-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.94) 0%,
    rgba(255, 255, 255, 0.82) 36%,
    rgba(255, 255, 255, 0.45) 70%,
    rgba(255, 255, 255, 0.12) 100%
  );
  pointer-events: none;
  z-index: 1;
}

.camp-showcase-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  flex-wrap: wrap;
}

.camp-showcase-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1 1 360px;
  min-width: 260px;
}

.camp-showcase-icon-wrap {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  box-shadow: 0 10px 22px rgba(16, 185, 129, 0.32);
}

.camp-showcase-info {
  flex: 1;
}

.camp-showcase-tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #047857;
  background: rgba(209, 250, 229, 0.92);
  border: 1px solid rgba(16, 185, 129, 0.25);
  padding: 3px 10px;
  border-radius: 999px;
  margin-bottom: 6px;
}

.camp-showcase-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.4px;
  line-height: 1.25;
  margin-bottom: 0;
}

.camp-showcase-right {
  flex-shrink: 0;
}

.camp-showcase-metric-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 175px;
  padding: 16px 26px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  text-align: center;
}

.camp-showcase-count {
  font-size: 50px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -1.5px;
  background: linear-gradient(135deg, #047857 0%, #059669 60%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.camp-showcase-metric-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-top: 4px;
}

.camp-showcase-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 3px 10px;
  border-radius: 999px;
  margin-top: 10px;
}

.status-pulse-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  animation: pulse-green 1.8s ease-in-out infinite;
}

@keyframes pulse-green {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(16, 185, 129, 0);
  }
}

.camp-showcase-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #047857;
  transition: color 0.2s ease;
}

.camp-showcase-arrow {
  color: #047857 !important;
  transition: transform 0.25s ease;
}

.camp-showcase-card:hover .camp-showcase-arrow {
  transform: translateX(4px);
}

/* ---------- KPI cards ---------- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.kpi-grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.kpi-card {
  position: relative;
  padding: 22px !important;
  border-radius: 18px !important;
  background: #ffffff !important;
  border: 1px solid #edf1f5 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04) !important;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease !important;
  overflow: hidden;
}

.kpi-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  height: 4px;
  background: var(--accent);
  opacity: 0.85;
}

.kpi-card--static {
  cursor: default;
}

.kpi-card:not(.kpi-card--static):hover {
  transform: translateY(-4px);
  border-color: var(--accent-soft) !important;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.1) !important;
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  box-shadow: 0 8px 18px var(--accent-soft);
}

.kpi-alert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  padding: 4px 10px;
  border-radius: 999px;
}

.kpi-alert-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f59e0b;
  animation: pulse 1.6s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(245, 158, 11, 0);
  }
}

.kpi-value {
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -1px;
}

.kpi-label {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #475569;
}

.kpi-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent);
}

.kpi-arrow {
  color: var(--accent) !important;
  transition: transform 0.25s ease;
}

.kpi-card:hover .kpi-arrow {
  transform: translateX(4px);
}

/* ---------- Mini stats ---------- */
.mini-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.mini-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #edf1f5;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}

.mini-card--link:hover {
  border-color: rgba(19, 115, 111, 0.25);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.mini-icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mini-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}

.mini-label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

/* ---------- Booking Calendar ---------- */
.booking-cal {
  margin-bottom: 24px;
}

.cal-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.cal-title-block {
  flex-shrink: 0;
}

.cal-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.cal-camp-select-wrap {
  min-width: 220px;
  max-width: 320px;
}

.cal-camp-select {
  font-size: 13px !important;
  border-radius: 10px !important;
}

.cal-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cal-nav-btn {
  color: #64748b !important;
}

.cal-nav-label-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
}

.cal-picker-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 4px 6px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.cal-picker-btn:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #13736f;
}

.cal-picker-icon {
  color: #64748b !important;
  transition: transform 0.2s ease;
}

.cal-picker-btn:hover .cal-picker-icon {
  color: #13736f !important;
}

.cal-picker-list {
  border-radius: 10px !important;
  padding: 4px !important;
}

.cal-picker-item {
  border-radius: 6px !important;
  min-height: 32px !important;
  font-size: 13px !important;
  cursor: pointer;
}

.cal-picker-item:hover {
  background: #f1f5f9 !important;
  color: #13736f !important;
}

.cal-picker-item--active {
  background: rgba(19, 115, 111, 0.12) !important;
  color: #13736f !important;
  font-weight: 700 !important;
}

.cal-month-label {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  min-width: 130px;
  text-align: center;
}

.cal-today-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  font-size: 13px !important;
  border: 1.5px solid #13736f !important;
}

/* Grid */
.cal-grid-wrap {
  overflow-x: auto;
}

.cal-dow-row {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #edf1f5;
  margin-bottom: 0;
}

.cal-dow {
  padding: 8px 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
}

.cal-weeks {
  display: flex;
  flex-direction: column;
}

.cal-week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-bottom: 1px solid #edf1f5;
}

.cal-week:last-child {
  border-bottom: none;
}

.cal-cell {
  min-height: 90px;
  padding: 8px 6px 6px;
  border-right: 1px solid #edf1f5;
  position: relative;
  transition: background 0.15s ease;
}

.cal-cell:last-child {
  border-right: none;
}

.cal-cell:hover {
  background: #f8fafc;
}

.cal-cell--faded {
  background: #fafafa;
}

.cal-cell--faded .cal-day-num {
  color: #cbd5e1;
}

.cal-cell--today {
  background: rgba(19, 115, 111, 0.04);
}

.cal-day-num {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
  line-height: 1;
}

.cal-day-num--today {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #13736f;
  color: #ffffff !important;
  font-size: 12px;
  font-weight: 800;
}

/* ---- Booking Span Bars ---- */
.cal-span-bar {
  position: relative;
  height: 26px;
  margin-bottom: 4px;
  background: var(--span-color-light, rgba(19,115,111,0.14));
  display: flex;
  align-items: center;
  overflow: hidden;
  cursor: default;
  transition: filter 0.15s ease;
  /* extend to cell edges on continuation days */
  margin-left: -6px;
  margin-right: -6px;
}

.cal-span-bar:hover {
  filter: brightness(0.96);
}

/* Start of span: thick accent bar on the left, rounded corners */
.cal-span-bar--start {
  border-left: 4px solid var(--span-color, #13736f);
  border-radius: 6px 0 0 6px;
  margin-left: 0;
  padding-left: 10px;
}

.cal-span-bar--end {
  border-radius: 0 6px 6px 0;
  margin-right: 0;
}

.cal-span-bar--single {
  border-left: 4px solid var(--span-color, #13736f);
  border-radius: 6px;
  margin-left: 0;
  margin-right: 0;
  padding-left: 10px;
}

.cal-span-name {
  font-size: 11px;
  font-weight: 700;
  color: var(--span-color, #13736f);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1;
}

/* Held (not yet confirmed) bookings: amber, striped, dashed outline */
.cal-span-bar--held {
  background: repeating-linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.22) 0 6px,
    rgba(245, 158, 11, 0.08) 6px 12px
  );
  border-top: 1px dashed rgba(180, 83, 9, 0.5);
  border-bottom: 1px dashed rgba(180, 83, 9, 0.5);
}
.cal-span-bar--held.cal-span-bar--start,
.cal-span-bar--held.cal-span-bar--single {
  border-left: 4px solid #d97706;
}
.cal-held-icon {
  color: #b45309 !important;
  margin-right: 3px;
  vertical-align: -1px;
}
.cal-held-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 4px 0;
  font-size: 12px;
  color: #92400e;
}
.cal-held-swatch {
  width: 22px;
  height: 12px;
  border-radius: 3px;
  border: 1px dashed rgba(180, 83, 9, 0.6);
  background: repeating-linear-gradient(135deg, rgba(245,158,11,0.3) 0 4px, rgba(245,158,11,0.1) 4px 8px);
}

/* ---- Calendar Legend ---- */
.cal-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  padding: 12px 4px 4px;
  border-top: 1px solid #edf1f5;
  margin-top: 8px;
}

.cal-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.cal-legend-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.cal-legend-name {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

@media (max-width: 600px) {
  .cal-cell {
    min-height: 64px;
    padding: 4px 3px 4px;
  }
  .cal-span-bar {
    height: 16px;
    margin-bottom: 2px;
  }
  .cal-span-name {
    font-size: 9px;
  }
  .cal-camp-select-wrap {
    min-width: 160px;
    max-width: 100%;
  }
  .cal-month-label {
    min-width: 100px;
    font-size: 13px;
  }
}

/* ---------- Chart ---------- */
.chart-card {
  padding: 24px 24px 16px !important;
  border-radius: 18px !important;
  background: #ffffff !important;
  border: 1px solid #edf1f5 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04) !important;
}

.chart-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.chart-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.chart-year {
  font-size: 12px;
  font-weight: 700;
  color: #13736f;
  background: rgba(19, 115, 111, 0.1);
  padding: 2px 10px;
  border-radius: 999px;
}

.chart-subtitle {
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
}

.chart-stats {
  display: flex;
  gap: 10px;
}

.chart-stat {
  min-width: 96px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #eef2f6;
}

.chart-stat-value {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.chart-stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.legend-line {
  display: inline-block;
  width: 18px;
  height: 3px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: 3px;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: -1px;
}

/* ---------- Payments ---------- */
.payment-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 24px 0 20px;
}

.payment-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #edf1f5;
}

.payment-stat-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-stat-body {
  min-width: 0;
}

.payment-stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-stat-label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

.charts-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 20px;
}

@media (max-width: 1264px) {
  .charts-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: -1px;
}

.chart-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 260px;
  color: #94a3b8;
  font-weight: 500;
}

.chart-placeholder p {
  margin: 0;
}

/* ---------- Responsive ---------- */
@media (max-width: 960px) {
  .dashboard {
    padding: 16px;
  }

  .hero {
    padding: 24px;
  }

  .hero-title {
    font-size: 24px;
  }
}

@media (max-width: 600px) {
  .dashboard {
    padding: 12px;
  }

  .hero {
    padding: 20px;
    border-radius: 16px;
  }

  .hero-title {
    font-size: 22px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .hero-actions,
  .hero-btn {
    width: 100%;
  }

  .camp-showcase-card {
    padding: 20px 18px !important;
    border-radius: 16px !important;
    background-position: center bottom !important;
  }

  .camp-showcase-overlay {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.94) 0%,
      rgba(255, 255, 255, 0.85) 45%,
      rgba(255, 255, 255, 0.4) 100%
    );
  }

  .camp-showcase-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .camp-showcase-icon-wrap {
    width: 48px;
    height: 48px;
  }

  .camp-showcase-title {
    font-size: 20px;
  }

  .camp-showcase-right {
    width: 100%;
  }

  .camp-showcase-metric-box {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    padding: 12px 18px;
  }

  .camp-showcase-count {
    font-size: 38px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .kpi-card {
    padding: 16px !important;
    border-radius: 14px !important;
  }

  .kpi-top {
    margin-bottom: 12px;
  }

  .kpi-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .kpi-alert {
    display: none;
  }

  .kpi-value {
    font-size: 26px;
  }

  .kpi-label {
    font-size: 13px;
  }

  .kpi-footer {
    margin-top: 12px;
    padding-top: 10px;
    font-size: 12px;
  }

  .mini-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .chart-card {
    padding: 18px 12px 12px !important;
  }

  .chart-stats {
    width: 100%;
  }

  .chart-stat {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
  }

  .payment-stats {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin: 16px 0 14px;
  }

  .payment-stat {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
  }

  .payment-stat-value {
    font-size: 17px;
  }

  .payment-stat-label {
    font-size: 12px;
  }

  .charts-grid {
    gap: 14px;
  }
}
</style>
